/**
 * Admin Layout Component - Detachable Admin Module
 *
 * Clean, minimal admin layout matching the main application design.
 * When extracting to a separate app, this component can be moved with minimal changes.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Navbar } from '../../components/Navbar/Navbar';
import BillingConfigModal from './BillingConfigModal';

interface AdminLayoutProps {
  children: React.ReactNode;
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const { user, switchRole, isAdminOnly } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const [showBillingConfig, setShowBillingConfig] = useState(false);

  const handleSwitchToUser = () => {
    switchRole('user');
    navigate('/dashboard');
  };

  const navActions = [
    {
      label: <Icon name={theme === 'dark' ? IconName.Sun : IconName.Moon} size="sm" variant="secondary" />,
      onClick: toggleTheme,
      tooltip: theme === 'dark' ? 'Light Mode' : 'Dark Mode',
    },
    ...(!isAdminOnly ? [
      {
        label: <Icon name={IconName.User} size="sm" variant="secondary" />,
        onClick: handleSwitchToUser,
        tooltip: 'Switch to User View',
      },
    ] : []),
    {
      label: <Icon name={IconName.Settings} size="sm" variant="secondary" />,
      onClick: () => setShowBillingConfig(true),
      tooltip: 'Billing Configuration',
    },
  ];

  return (
    <div className="h-screen bg-bg-secondary flex flex-col">
      <Navbar
        brandName="BlueGenAI Admin"
        user={user}
        onHomeClick={() => navigate('/admin')}
        actions={navActions}
      />
      <div className="flex-1 min-h-0 overflow-hidden">
        <div className="max-w-6xl w-full mx-auto h-full flex flex-col min-h-0">
          <div className="flex-1 min-h-0 overflow-hidden pr-bg-v2-xxs pb-bg-v2-5xl pt-bg-v2-5xl">
            {children}
          </div>
        </div>
      </div>

      <BillingConfigModal
        isOpen={showBillingConfig}
        onClose={() => setShowBillingConfig(false)}
        onSuccess={() => {}}
      />
    </div>
  );
};

export default AdminLayout;